export interface TickerCallback {
  (delta: number): void;
}

export class TickerHandle {
  public next?: TickerHandle;
  public disposed = false;

  constructor(public fn: TickerCallback) {}
  dispose() {
    this.disposed = true;
  }
}

export class Ticker {
  protected _animationFrame = -1;
  protected _stopped: boolean;
  protected _previousTime = Date.now();
  protected _callbacks?: TickerHandle;

  /**
   *
   * @param {Boolean} start (optional) If the ticker should be automatically running when constructed.
   */
  constructor(start = false) {
    this._animationFrame = -1;
    this._stopped = !start;
    this._previousTime = Date.now();
  }

  subscribe(cb: TickerCallback) {
    const handle = new TickerHandle(cb);
    const prev = this._callbacks;
    handle.next = prev;
    this._callbacks = handle;
    return handle;
  }

  clear() {
    this.stop();
    this._callbacks = undefined;
  }

  stop() {
    if (!this._stopped) {
      this._stopped = true;
      // TODO: Clear out all callbacks which haven't been processed.
      cancelAnimationFrame(this._animationFrame);
      let callback = this._callbacks;
      let prev = null;
      while (callback != null) {
        if (callback.disposed) {
          if (prev != null) {
            // Remove this item from the list.
            prev.next = callback.next;
          }
        }
        prev = callback;
        callback = callback.next;
      }
    }
  }

  start() {
    if (this._stopped) {
      this._stopped = false;
      this._previousTime = Date.now();
      this._onUpdate();
    }
  }

  /**
   * The update loop.
   * Runs through the linked list of subscribers and updates them
   * with a callback function, passing in the delta time as the only param.
   */
  _onUpdate() {
    const now = Date.now();
    const dt = (now - this._previousTime) / 1000;
    this._previousTime = now;

    let callback = this._callbacks;
    let prev: TickerHandle | undefined = undefined;
    while (callback != null) {
      if (!callback.disposed) {
        callback.fn(dt);
      } else {
        if (prev != null) {
          // Remove this item from the list.
          prev.next = callback.next;
        }
      }
      prev = callback;
      callback = callback.next;
    }
    this._animationFrame = requestAnimationFrame(dt => this._onUpdate());
  }

  get stopped() {
    return this._stopped;
  }
}
